import { useCallback, useMemo } from 'react';

import { type IFileViewerProps } from './FileViewer'
import {
  Components as defaultComponents,
  type FileViewerComponents,
} from './FileViewer.components'
import { strings as defaultStrings } from './FileViewer.strings'
import { type FileViewerContext } from './FileViewerContext'
import { type IFileData } from '../File/File'

interface UseFileViewerOptions<FileType = IFileData> {
  files: IFileViewerProps<FileType>['files']
  selectedFile: IFileViewerProps<FileType>['selectedFile']
  onSelectedFileChange: IFileViewerProps<FileType>['onSelectedFileChange']
  getFileData: IFileViewerProps<FileType>['getFileData']
  isFileSelected: IFileViewerProps<FileType>['isFileSelected']
  components: Partial<FileViewerComponents<FileType>>
  strings: IFileViewerProps<FileType>['strings']
}

export const useFileViewer = <FileType = IFileData>({
  files = [],
  selectedFile,
  onSelectedFileChange,
  getFileData,
  isFileSelected,
  components,
  strings,
}: UseFileViewerOptions<FileType>) => {
  const selectFile = useCallback((file: FileType) => {
    if (file === selectedFile) return

    onSelectedFileChange?.(file)
  }, [])

  const Components = useMemo<FileViewerComponents<FileType>>(
    () => ({
      ...defaultComponents,
      ...components,
    }),
    [components]
  )

  const contextValues: FileViewerContext<FileType> = useMemo(
    () => ({
      files,
      selectedFile,
      selectFile,
      getFileData: getFileData || ((file) => file),
      isFileSelected,
      Components,
      strings: {
        ...defaultStrings,
        ...strings,
      },
    }),
    [files, Components, selectedFile, selectFile, getFileData]
  )

  return {
    contextValues,
  }
}
